import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';

import useMatchMedia from 'utils/hooks/useMatchMedia';
import theme from 'mui/base/theme/index';

import { pushGTMChooseLocationEvent, pushGTMEvent, types } from 'utils/gtm';
import NewsBanner, { shouldDisplayNewsBannerInBooking } from 'components/common/NewsBanner';
import DS_Tabs from 'mui/components/elements/DS_Tabs';
import DS_Map from 'mui/components/elements/DS_Map';
import Link from 'mui/components/elements/Link';
import Loader from 'components/Booking-new/modules/Steps/shared/Loader';

import { useDSTabs } from 'mui/components/elements/DS_Tabs/utils/useDSTabs';
import translationsLocations from 'mui/components/sections/DS_LocationHero/elements/data/translationsLocations';
import { useGeolocationInput } from '../../../../../../../../hooks/locations';
import { useLocationStep } from '../../../../../../utils/steps/location';

import { StepLocationCommonPropTypes } from '../../types';

import LocationsList from '../LocationsList';
import SearchBoxWoosmap from '../SearchBoxWoosmap';
import StepHeader from '../../../../shared/StepHeader';

import {
  useLocationRefs,
  useSelectLocationScroll,
  useWaitlistEffector,
  useVirtualLocationScroll,
  useFilterDates,
} from '../../../../../../../../mui/components/sections/DS_FindInMap/hooks';
import useLocationServicePairs from '../../../../../../utils/steps/location/useLocationServicePairs';
import { useLocationTimeSlotV3Selection } from '../../../../../../utils/steps/location/useLocationTimeSlotV3Selection';
import DS_DailyFilters from '../../../../../../../../mui/components/sections/DS_FindInMap/elements/DS_DailyFilters';

import useBodyScrollHandler from '../../../../../../utils/steps/location/useBodyScrollHandler';
import {
  LinkWrapper,
  StyledBannerWrapper,
  StyledLocationsWrapper,
  StyledMapLoaderWrapper,
  StyledMapWrapper,
  StyledMobileControls,
  StyledTabsWrapper,
  StyledWrapper,
  StyledAsteriskFootnote,
} from './styles';

export const LocationsViewAliases = {
  list: 'list',
  map: 'map',
};

function ChooseLocationMobile({
  stepLocation,
  allLocations,
  closestLocationsState,
  selectedLocationState,
  resetSelectedLocation,
  changeServiceLink,
  showNewsBanner,
  funnelType,
  navigation,
  rescheduleData,
  serviceCode,
}) {
  const desktopBreakpoint = theme.breakpoints.values.lg;
  const isDesktop = useMatchMedia(`(min-width: ${desktopBreakpoint}em)`);

  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isGeoSearched, setIsGeoSearched] = useState(false);
  const mapOptions = {
    fullView: true,
    infoWindows: true,
    hideMarkers: process.env.GATSBY_COUNTRY_CODE === 'de-AT',
  };

  const locationViewOptions = [
    {
      id: LocationsViewAliases.list,
      label: stepLocation?.mobileListTabName || 'All locations',
    },
    {
      id: LocationsViewAliases.map,
      label: stepLocation?.mobileMapTabName || 'Map',
    },
  ];
  const onMapTabSelection = () => {
    if (activeTab.id === LocationsViewAliases.map)
      pushGTMEvent({
        eventAction: 'tap on map',
        eventCategory: 'Booking funnel',
      });
  };
  const { activeTabIdx, activeTab, onTabChange } = useDSTabs({
    model: locationViewOptions,
    defaultIdx: 0,
    callback: onMapTabSelection,
  });

  const newsBannerVisible = shouldDisplayNewsBannerInBooking(stepLocation?.newsBanner, showNewsBanner);

  const locations = closestLocationsState[0];

  const { options, gotLocations, setGotLocations, isGeoLoading, getGeoLocation, error } = useGeolocationInput(
    locations,
    stepLocation.sortingAlgorithm,
  );

  const { nextAppointment: nextAppointmentLabel } = translationsLocations[process.env.GATSBY_COUNTRY_CODE];
  const { selectLocation } = useLocationStep();
  const onLocationSelection = location => {
    pushGTMChooseLocationEvent(location.code, funnelType);
    selectLocation(location);
  };

  const mapMetaData = {
    allLocations,
    nextAppointmentLabel,
    flagshipLocationLabel: stepLocation?.useFlagshipStoreLabel,
    useFlagshipStoreLabel: stepLocation?.useFlagshipStoreLabel,
    bookingButton: {
      title: stepLocation?.bookingButtonLabel,
    },
    onLocationSelection,
    isBookingFunnel: true,
    bookingFunnelSelectedLocation: selectedLocationState[0],
  };

  const timeLocationData = {
    showTimeSlots: stepLocation?.showTimeSlots,
    noDatesAvailableLT: stepLocation?.noDatesAvailableLT,
    waitlistButtonLabelLT: stepLocation?.waitlistButtonLabelLT,
    confirmationDialogLabelLT: stepLocation?.confirmationDialogLabelLT,
    cancelDialogLabelLT: stepLocation?.cancelDialogLabelLT,
    waitlistSuccessLinkLT: stepLocation?.waitlistSuccessLinkLT,
    navigation,
  };

  const selectedLocation = selectedLocationState[0];

  const [mounted, setMounted] = useState(false);
  const { locationsWrapperRef, locationsRefs } = useLocationRefs(allLocations);

  const [displayedLocations, displayedItemCodes, scrolled, allItemsDisplayed] = useVirtualLocationScroll(
    locationsWrapperRef,
    locations,
    3,
    selectedLocation,
    mapMetaData.isBookingFunnel,
    isGeoSearched,
  );

  // process the locations scroll
  useSelectLocationScroll({
    locationsWrapperRef,
    locationsRefs,
    selectedLocation,
    isBookingFunnel: true,
    mounted,
  });

  const { shouldPersonalDataCollected } = useWaitlistEffector(timeLocationData.showTimeSlots, true);

  const { locationServicePairs, handleSlotsFetched } = useLocationServicePairs(
    isGeoSearched ? locations : displayedLocations,
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if ((gotLocations && !error) || params.has('city') || params.has('state')) setIsGeoSearched(true);
  }, [error, gotLocations, options, isGeoSearched, allItemsDisplayed]);

  const desiredDays = stepLocation?.showTimeSlots ? 2 : 1;
  const desiredSlots = stepLocation?.showTimeSlots ? 4 : 1;

  const notFetch = !stepLocation?.showTimeSlots && !isDesktop;

  const type = rescheduleData ? types.reschedule : types.book;
  const resultsForGTM = { type, serviceCode, funnelType };

  const { sortedDates, selectedDates, setSelectedDates, resetDates, setResetDates } =
    useFilterDates(stepLocation?.showDailyFilters) || {};

  const { slots, locationLoadingStatus } = useLocationTimeSlotV3Selection(
    locationServicePairs,
    handleSlotsFetched,
    navigation,
    shouldPersonalDataCollected,
    timeLocationData.waitlistSuccessLinkLT,
    desiredDays,
    desiredSlots,
    displayedItemCodes,
    resultsForGTM,
    selectedDates,
    stepLocation?.showDailyFilters,
    notFetch,
  );

  const filtersDatesProps = {
    showDailyFilters: stepLocation?.showDailyFilters,
    filtersEmpty: selectedDates?.length === 0,
    selectedDates: selectedDates,
    resetDates: resetDates,
  };

  const { bodyScroll } = useBodyScrollHandler();

  useEffect(() => {
    const applyStyles = (element, styles) => {
      Object.keys(styles).forEach(style => {
        element.style[style] = styles[style];
      });
    };

    const styles = {
      overflowY: 'hidden',
      position: 'relative',
      height: '100%',
    };

    applyStyles(document.body, styles);
    applyStyles(document.documentElement, styles);

    return () => {
      const resetStyles = {
        overflowY: '',
        position: '',
        height: '',
      };
      applyStyles(document.body, resetStyles);
      applyStyles(document.documentElement, resetStyles);
    };
  }, []);

  const mobileElementsClass = scrolled || bodyScroll ? 'fade dissapear' : 'fade';

  return (
    <StyledWrapper>
      <StyledMobileControls>
        <div className={mobileElementsClass}>{stepLocation?.title && <StepHeader title={stepLocation.title} />}</div>
        {changeServiceLink?.url && (
          <LinkWrapper className={mobileElementsClass}>
            <Typography component={Link} variant="DS_labelSmallRegular" url={changeServiceLink.url}>
              <span>{changeServiceLink.title}</span>
            </Typography>
          </LinkWrapper>
        )}

        <SearchBoxWoosmap
          placeholder={stepLocation?.searchPlaceholder}
          setClosestLocations={closestLocationsState[1]}
          resetSelectedLocation={resetSelectedLocation}
          isMapLoaded={true}
          getGeoLocation={getGeoLocation}
          setGotLocations={setGotLocations}
          gotLocations={gotLocations}
          isGeoLoading={isGeoLoading}
          setIsGeoSearched={setIsGeoSearched}
          sortingAlgorithm={stepLocation?.sortingAlgorithm}
        />

        {newsBannerVisible && (
          <StyledBannerWrapper data-test="news-banner" className={mobileElementsClass}>
            <NewsBanner newsBanner={stepLocation?.newsBanner} alert={true} />
          </StyledBannerWrapper>
        )}
      </StyledMobileControls>
      <StyledTabsWrapper>
        <DS_Tabs
          className="booking-locations-view"
          array={locationViewOptions}
          bullets={false}
          onChange={onTabChange}
          defaultValue={activeTabIdx}
          renderItem={view =>
            view.id === LocationsViewAliases.list ? (
              <StyledLocationsWrapper>
                {stepLocation?.showDailyFilters && stepLocation?.showTimeSlots && (
                  <DS_DailyFilters
                    sortedDates={sortedDates}
                    selectedDates={selectedDates}
                    setSelectedDates={setSelectedDates}
                    setResetDates={setResetDates}
                  />
                )}
                <LocationsList
                  setMounted={setMounted}
                  locations={gotLocations && options.length !== 0 ? options : locations}
                  distancePlaceholder={stepLocation?.distancePlaceholder}
                  bookingButtonLabel={stepLocation?.bookingButtonLabel}
                  selectedLocationState={selectedLocationState}
                  useFlagshipStoreLabel={stepLocation.useFlagshipStoreLabel}
                  allLocations={allLocations}
                  timeLocationData={timeLocationData}
                  navigation={navigation}
                  isGeoSearched={isGeoSearched}
                  locationsRefs={locationsRefs}
                  ref={locationsWrapperRef}
                  slots={slots}
                  shouldPersonalDataCollected={shouldPersonalDataCollected}
                  displayedLocations={displayedLocations}
                  loading={locationLoadingStatus[location.code]}
                  filtersDatesProps={filtersDatesProps}
                  allItemsDisplayed={allItemsDisplayed}
                  useDiscountOnFlagship={stepLocation?.useDiscountOnFlagship}
                  discountFlagshipText={stepLocation?.discountFlagshipText}
                />
              </StyledLocationsWrapper>
            ) : (
              <StyledMapWrapper data-test="booking-location-map-container">
                {!isMapLoaded && (
                  <StyledMapLoaderWrapper>
                    <Loader />
                  </StyledMapLoaderWrapper>
                )}
                <DS_Map
                  locations={gotLocations && options.length !== 0 ? options : locations}
                  setIsMapLoaded={setIsMapLoaded}
                  options={mapOptions}
                  metaData={mapMetaData}
                  selectedLocation={selectedLocationState[0]}
                  setSelectedLocation={selectedLocationState[1]}
                  isMapLoaded={isMapLoaded}
                  showTimeSlots={stepLocation?.showTimeSlots}
                ></DS_Map>
              </StyledMapWrapper>
            )
          }
        ></DS_Tabs>
        {stepLocation?.useDiscountOnFlagship && stepLocation?.asteriskFootnote?.asteriskFootnote && (
          <StyledAsteriskFootnote className="discount-asterisk">
            <Typography variant="DS_copyXSmallRegular" component="p">
              {stepLocation?.asteriskFootnote?.asteriskFootnote}
            </Typography>
          </StyledAsteriskFootnote>
        )}
      </StyledTabsWrapper>
    </StyledWrapper>
  );
}

ChooseLocationMobile.propTypes = {
  ...StepLocationCommonPropTypes,
};

export default ChooseLocationMobile;
